import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { currencyCountriesApi, type CountryListItem as ApiCountry } from '../api/currencyCountries'
import {
  onboardingApi,
  OnboardingDocTypeCodes,
  OnboardingPersonRoleCodes,
  type AdminOnboardingDetailsDto,
  type AdminOnboardingDocumentDto,
  type AdminOnboardingPersonDto,
} from '../api/onboarding'
import { getErrorMessage } from '../api/client'
import { useAdminAuthStore } from '../store/adminAuthStore'
import {
  SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY,
  clampShareholdersToBeneficialOwnerOnly,
  emptyIndividualStakeholder,
  formatPersonApiFromStakeholder,
  isShareholderOwnershipTotalValid,
  normalizeStakeholderRow,
  shareholderPercentTotal,
  shouldValidateShareholdersTotal100,
  STAKEHOLDER_ROLE_OPTIONS,
  shareholderCompositeName,
  shareholderRowCountsAsDirector,
  validateIndividualStakeholder,
  type IndividualStakeholderRow,
} from '../onboarding/business/businessStakeholderTypes'
import { IndividualStakeholderFormFields } from '../onboarding/business/StakeholderFields'

type ClientOnboardingDetailsDto = AdminOnboardingDetailsDto
type ClientOnboardingDocumentDto = AdminOnboardingDocumentDto
type ClientOnboardingPersonDto = AdminOnboardingPersonDto

/** Stages: profile → entity → narrative → shareholders → directors → documents → review. */
const STEP_LABELS = [
  'Company size',
  'Monthly volume (USD)',
  'Incoming transactions',
  'Outgoing transactions',
  'Areas of interest',
  'Entity information',
  'Business description',
  'Shareholders',
  'Directors',
  'Documents',
  'Review',
] as const

const FILE_MAX_BYTES = 28 * 1024 * 1024
const ALLOW_EXT = /\.(pdf|jpg|jpeg|png)$/i
const DOC_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/*'

const EMPLOYEE_BANDS = ['1-10', '11-100', '101-200', '200-500', '500+'] as const

const USD_VOLUME_BANDS = ['<50k', '50k-100k', '100k-250k', '250k-500k', '500k-1M', '1M-5M', '>5M'] as const

const TX_BANDS = ['0', '1-5', '6-20', '21-50', '51-100', '101-500', '501-1000', '1001-5000', '5001-10000', '10000+'] as const

const INTERESTS = [
  'Local or international bank account opening',
  'Card payments and local payment methods from customers',
  'Currency conversion and domestic/international transfers',
  'Multi-currency corporate and employee cards',
  'Company card expenses, reimbursements and bills management',
  'API solutions and financial products',
  'E-commerce transactions',
  'Investment and securities trading',
  'Remittance and sending money abroad',
  'Business expenses and everyday transactions',
] as const

const ENTITY_TYPES = ['sole_proprietorship', 'limited_liability_company', 'partnership', 'corporation', 'other'] as const

function docMatchesCode(d: ClientOnboardingDocumentDto, code: string): boolean {
  return (d.documentType ?? '').toUpperCase() === code.toUpperCase()
}

/** Company-level legacy `PROOF_OF_ADDRESS` uploads have no linked person row. */
function hasBusinessLevelAddressProof(documents: ClientOnboardingDocumentDto[]): boolean {
  const hasPid = (d: ClientOnboardingDocumentDto) =>
    d.personId != null && typeof d.personId === 'string' && d.personId.trim().length > 0
  return (
    documents.some((d) => docMatchesCode(d, OnboardingDocTypeCodes.ProofOfBusinessAddress)) ||
    documents.some((d) => docMatchesCode(d, OnboardingDocTypeCodes.ProofOfAddress) && !hasPid(d))
  )
}

function hasPersonScopedDoc(documents: ClientOnboardingDocumentDto[], personId: string, code: string): boolean {
  return documents.some((d) => docMatchesCode(d, code) && d.personId != null && String(d.personId) === personId)
}

function formatDocPersonCaption(personId: string | null | undefined, personsList: ClientOnboardingPersonDto[]): string | null {
  if (personId == null || String(personId).trim() === '') return null
  const p = personsList.find((x) => x.id === personId)
  return p?.fullName ?? null
}

type BusinessDocumentGuideRow = {
  code: string
  label: string
  required: boolean
  hint: string | null
  personRole: 'director' | 'ubo' | null
}

const BUSINESS_DOCUMENT_GUIDE: BusinessDocumentGuideRow[] = [
  {
    code: OnboardingDocTypeCodes.CertificateOfIncorporation,
    label: 'Certificate of Incorporation',
    required: true,
    hint: null,
    personRole: null,
  },
  {
    code: OnboardingDocTypeCodes.BusinessRegistration,
    label: 'Business Registration',
    required: false,
    hint: null,
    personRole: null,
  },
  {
    code: OnboardingDocTypeCodes.ProofOfBusinessAddress,
    label: 'Proof of Business Address (issued within last 3 months)',
    required: true,
    hint: 'Corporate address verification (not tied to an individual director/UBO profile).',
    personRole: null,
  },
  {
    code: OnboardingDocTypeCodes.DirectorId,
    label: 'ID Card / Passport – Director',
    required: true,
    hint: 'One upload per director.',
    personRole: 'director',
  },
  {
    code: OnboardingDocTypeCodes.UboId,
    label: 'ID Card / Passport – Ultimate Beneficial Owner (UBO)',
    required: true,
    hint: 'One upload per shareholder / beneficial owner.',
    personRole: 'ubo',
  },
  {
    code: OnboardingDocTypeCodes.AddressProofDirector,
    label: 'Address Proof – Director',
    required: true,
    hint: 'One proof per director.',
    personRole: 'director',
  },
  {
    code: OnboardingDocTypeCodes.AddressProofShareholder,
    label: 'Address Proof – Shareholder',
    required: true,
    hint: 'One proof per beneficial owner (shareholder).',
    personRole: 'ubo',
  },
  {
    code: OnboardingDocTypeCodes.ArticlesOfAssociation,
    label: 'Articles of Association',
    required: true,
    hint: null,
    personRole: null,
  },
  {
    code: OnboardingDocTypeCodes.BusinessProofActivity,
    label: 'Business Proof – Invoice, Sales Contract, Shipping Documents',
    required: false,
    hint: 'Preferably issued within the last 3 months.',
    personRole: null,
  },
  {
    code: OnboardingDocTypeCodes.Other,
    label: 'Other supporting documents',
    required: false,
    hint: null,
    personRole: null,
  },
]

/** Opens the native file picker via `input.click()` so the hidden input does not receive focus (label `htmlFor` focus → browser scroll/jump on long steps). */
function BusinessDocUploadButton(props: {
  accept: string
  disabled: boolean
  onFile: (file: File) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { accept, disabled, onFile } = props
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        tabIndex={-1}
        className="sr-only"
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.currentTarget.value = ''
          if (f) onFile(f)
        }}
      />
      <button
        type="button"
        disabled={disabled}
        className={`inline-flex rounded-md border border-[#C1C7D3] bg-white px-3 py-1.5 text-xs font-semibold text-[#25325A] hover:bg-[#F1F4F8] ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        onClick={() => inputRef.current?.click()}
      >
        Upload
      </button>
    </>
  )
}

function documentTypeDisplayLabel(documentTypeRaw: string | undefined): string {
  const c = (documentTypeRaw ?? '').toUpperCase()
  const row = BUSINESS_DOCUMENT_GUIDE.find((g) => g.code.toUpperCase() === c)
  if (row) return row.label
  if (c === OnboardingDocTypeCodes.ProofOfAddress.toUpperCase())
    return 'Proof of Business Address / address (legacy upload)'
  return (documentTypeRaw ?? '').replace(/_/g, ' ') || 'Document'
}

function entityTypeDisplayLabel(internalValue: string): string {
  const spaced = internalValue.replace(/_/g, ' ').trim()
  if (!spaced) return internalValue
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** Entity-step fields sourced from GET /Currency/get-countries (countries table); value stored as country name to match portal/KYC payloads. */
const ENTITY_COUNTRY_SELECT_KEYS = new Set(['incorporationCountry', 'regCountry'])

/** Entity step: country + legal name + entity type, then IDs/registered office, then office checkbox/line, then contact & industry. */
const ENTITY_FIELD_KEYS_HEAD_LEAD = [
  ['incorporationCountry', 'Country of incorporation'],
  ['legalEntityName', 'Legal entity name'],
] as const

const ENTITY_FIELD_KEYS_HEAD_REST = [
  ['registrationNumber', 'Registration number'],
  ['taxId', 'Tax ID'],
  ['regAddressLine', 'Registered address — street'],
  ['regCity', 'City'],
  ['regPostal', 'Postal code'],
  ['regCountry', 'Country (registered office)'],
] as const

const ENTITY_FIELD_KEYS_TAIL = [
  ['phone', 'Phone'],
  ['tradingNames', 'Trading name(s)'],
  ['website', 'Website'],
  ['mainIndustry', 'Main industry'],
  ['additionalIndustry', 'Additional industry'],
] as const

const FUNDS_CORRIDOR_FIELDS = [
  { key: 'incomingCountries' as const, label: 'Incoming funds from' },
  { key: 'outgoingCountries' as const, label: 'Outgoing funds to' },
]

const INCOME_SOURCE_OPTIONS = [
  { value: 'sales_goods', label: 'Sales of goods / products' },
  { value: 'service_fees', label: 'Service fees / professional services' },
  { value: 'subscription', label: 'Subscription / recurring revenue' },
  { value: 'investment_trading', label: 'Investment / trading income' },
  { value: 'rental_leasing', label: 'Rental / leasing' },
  { value: 'parent_shareholder_funding', label: 'Funding from parent company / shareholders' },
  { value: 'loans_credit', label: 'Loans / credit' },
  { value: 'other', label: 'Other (specify)' },
] as const

const INCOME_SOURCE_VALUES = new Set<string>(INCOME_SOURCE_OPTIONS.map((o) => o.value))

function parseStoredCountryNames(raw: string | undefined): string[] {
  if (!raw?.trim()) return []
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
}

function joinStoredCountryNames(sortedNames: string[]): string {
  return sortedNames.map((s) => s.trim()).filter(Boolean).join(', ')
}

export type BusinessDraftV1 = {
  version: 1
  profile?: {
    employeeBand?: string
    monthlyUsdVolumeBand?: string
    incomingTxBand?: string
    outgoingTxBand?: string
    interests?: string[]
  }
  entity?: Record<string, string | boolean | undefined>
  ownership?: {
    shareholderRows?: IndividualStakeholderRow[]
    extraDirectorRows?: IndividualStakeholderRow[]
    /** Nominee / third‑party: yes/no; details only when Yes */
    nomineeThirdPartyAnswer?: 'yes' | 'no' | ''
    nomineeQuestion?: string
    /** Whether shareholders align with directors — retained for questionnaire / compliance context */
    shareholdersAreDirectors?: 'yes' | 'no' | ''
    /** When shareholdersAreDirectors is yes: all shareholders are directors vs only flagged rows (`alsoDirector`) */
    shareholdersDirectorCoverage?: '' | 'all' | 'some'
    /** Legacy JSON export (no longer edited in UI when shareholderRows present) */
    shareholdersJson?: string
    directorsJson?: string
  }
}

function defaultDraft(): BusinessDraftV1 {
  return {
    version: 1,
    profile: { interests: [] },
    entity: {
      incorporationCountry: '',
      legalEntityName: '',
      registrationNumber: '',
      taxId: '',
      regAddressLine: '',
      regCity: '',
      regPostal: '',
      regCountry: '',
      officeSameAsReg: false,
      officeAddressLine: '',
      entityType: 'limited_liability_company',
      entityTypeOther: '',
      phone: '',
      tradingNames: '',
      website: '',
      mainIndustry: '',
      additionalIndustry: '',
      productsDescription: '',
      incomingCountries: '',
      outgoingCountries: '',
      incomeSource: '',
      incomeSourceOther: '',
    },
    ownership: {
      shareholderRows: [emptyIndividualStakeholder()],
      extraDirectorRows: [],
      nomineeThirdPartyAnswer: '',
      nomineeQuestion: '',
      shareholdersAreDirectors: '',
      shareholdersDirectorCoverage: '',
      shareholdersJson: '',
      directorsJson: '',
    },
  }
}

function parseDraft(raw: string | null | undefined): BusinessDraftV1 {
  if (!raw || !raw.trim()) return defaultDraft()
  try {
    const o = JSON.parse(raw) as BusinessDraftV1
    if (!o || o.version !== 1) return defaultDraft()
    const merged = { ...defaultDraft(), ...o, version: 1 as const }
    const ow = { ...defaultDraft().ownership, ...(o.ownership ?? {}) }
    let shareholderRows =
      Array.isArray(ow.shareholderRows) && ow.shareholderRows.length > 0
        ? ow.shareholderRows.map((r) => normalizeStakeholderRow(r as Partial<IndividualStakeholderRow>))
        : undefined
    if (!shareholderRows) shareholderRows = [emptyIndividualStakeholder()]
    if (SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY) shareholderRows = clampShareholdersToBeneficialOwnerOnly(shareholderRows)
    const extraDirectorRows = Array.isArray(ow.extraDirectorRows)
      ? ow.extraDirectorRows.map((r) => normalizeStakeholderRow(r as Partial<IndividualStakeholderRow>))
      : []
    merged.ownership = { ...ow, shareholderRows, extraDirectorRows }
    const ownSrcRaw = (o as { ownership?: Record<string, unknown> }).ownership
    const coverageKeyPresent =
      !!ownSrcRaw &&
      typeof ownSrcRaw === 'object' &&
      Object.prototype.hasOwnProperty.call(ownSrcRaw, 'shareholdersDirectorCoverage')
    if (merged.ownership.shareholdersAreDirectors === 'yes' && !coverageKeyPresent)
      merged.ownership = { ...merged.ownership, shareholdersDirectorCoverage: 'all' }
    const nto = merged.ownership.nomineeThirdPartyAnswer
    if (
      nto !== 'yes' &&
      nto !== 'no' &&
      String(merged.ownership.nomineeQuestion ?? '').trim()
    )
      merged.ownership = { ...merged.ownership, nomineeThirdPartyAnswer: 'yes' }
    return merged
  } catch {
    /* fallthrough */
  }
  return defaultDraft()
}

/** When the client saved company via `company_info` but entity fields in extended JSON are still empty, pre-fill the wizard. */
function mergeCompanySnapshotIntoDraft(
  draft: BusinessDraftV1,
  company:
    | {
        companyName: string
        registrationNumber: string
        countryOfIncorporation: string
        businessActivity: string
        website?: string | null
      }
    | null
    | undefined
): BusinessDraftV1 {
  if (!company) return draft
  const ent = { ...(draft.entity ?? {}) }
  const isEmpty = (v: unknown) => !String(v ?? '').trim()
  if (isEmpty(ent.legalEntityName) && company.companyName?.trim()) {
    ent.legalEntityName = company.companyName.trim()
  }
  if (isEmpty(ent.registrationNumber) && company.registrationNumber?.trim()) {
    ent.registrationNumber = company.registrationNumber.trim()
  }
  if (isEmpty(ent.incorporationCountry) && company.countryOfIncorporation?.trim()) {
    ent.incorporationCountry = company.countryOfIncorporation.trim()
  }
  if (isEmpty(ent.regCountry) && company.countryOfIncorporation?.trim()) {
    ent.regCountry = company.countryOfIncorporation.trim()
  }
  if (isEmpty(ent.productsDescription) && company.businessActivity?.trim()) {
    ent.productsDescription = company.businessActivity.trim()
  }
  if (isEmpty(ent.website) && company.website?.trim()) {
    ent.website = company.website.trim()
  }
  return { ...draft, entity: ent }
}

export default function AdminBusinessOnboardingWizardPage() {
  const { applicationId: routeApplicationId } = useParams<{ applicationId: string }>()
  const adminUser = useAdminAuthStore((s) => s.adminUser)
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<BusinessDraftV1>(defaultDraft)
  const [portalDetails, setPortalDetails] = useState<ClientOnboardingDetailsDto | null>(null)
  const [applicationId, setApplicationId] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  const [pageLoading, setPageLoading] = useState(true)
  const [pageError, setPageError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const footerMsgRef = useRef<HTMLDivElement | null>(null)
  /** Scroll target when Save fails because shareholder % ≠ 100% — same message lives in-banner, no duplicate alert text */
  const ownershipTotalBannerRef = useRef<HTMLParagraphElement | null>(null)

  const refreshApplication = useCallback(() => {
    setReloadToken((x) => x + 1)
  }, [])

  /** Validation messages sit next to the action row; scroll them into view on long wizard steps */
  useEffect(() => {
    if (!msg) return
    footerMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [msg])

  /** Edit linked shareholder FIO from directors step (modal). */
  const [directorFioModalIx, setDirectorFioModalIx] = useState<number | null>(null)
  const [directorFioDraft, setDirectorFioDraft] = useState({ firstName: '', middleName: '', lastName: '' })

  const [downloadBusyId, setDownloadBusyId] = useState<string | null>(null)

  /** Reference catalogue: same API as payouts/contacts (`/Currency/get-counties` → countries table). */
  const [referenceCountries, setReferenceCountries] = useState<ApiCountry[]>([])
  const [countriesLoadError, setCountriesLoadError] = useState(false)
  /** Controlled “Add country…” selects for funds corridor tag pickers (reset after add). */
  const [fundsCountryPicker, setFundsCountryPicker] = useState<{
    incomingCountries: string
    outgoingCountries: string
  }>({ incomingCountries: '', outgoingCountries: '' })

  const auditUserId = adminUser?.id ?? ''

  const application = portalDetails?.application ?? null

  const isBusiness = useMemo(() => (application?.applicationType ?? '').toUpperCase() === 'BUSINESS', [application])

  /** Avoid full-screen loader after first snapshot for this application (reloadToken / refresh refreshes docs/persons in background without flashing away the step). */
  const detailsHydratedForAppRef = useRef<string | null>(null)

  const load = useCallback(async () => {
    const rid = routeApplicationId?.trim()
    if (!rid || !auditUserId) {
      detailsHydratedForAppRef.current = null
      setApplicationId(null)
      setPortalDetails(null)
      setPageLoading(false)
      setPageError(!auditUserId ? 'Admin session missing.' : 'Application id missing.')
      return
    }

    const needBlockingSpinner = detailsHydratedForAppRef.current !== rid
    if (needBlockingSpinner) setPageLoading(true)

    setPageError(null)
    setMsg(null)
    try {
      const d = await onboardingApi.getDetails(rid)
      if ((d.application.applicationType ?? '').toUpperCase() !== 'BUSINESS') {
        setPageError('This editor supports BUSINESS applications only.')
        setPortalDetails(null)
        setApplicationId(null)
        detailsHydratedForAppRef.current = null
        return
      }
      setApplicationId(d.application.id)
      setPortalDetails(d)
      detailsHydratedForAppRef.current = rid

      /** Only hydrate questionnaire from DB on first snapshot; afterwards keep local draft to avoid overwriting between steps except when application id changes. */
      const serverDraft = parseDraft(d.application.extendedQuestionnaireJson ?? null)
      if (needBlockingSpinner) {
        setDraft(mergeCompanySnapshotIntoDraft(serverDraft, d.companyInfo))
      }
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
      setPageError(getErrorMessage(e))
    } finally {
      setPageLoading(false)
    }
  }, [routeApplicationId, auditUserId])

  useEffect(() => {
    void load()
  }, [load, reloadToken])

  useEffect(() => {
    let cancelled = false
    void currencyCountriesApi.getCountries().then(
      (res) => {
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : []
        setReferenceCountries(list)
        setCountriesLoadError(false)
      },
      () => {
        if (cancelled) return
        setReferenceCountries([])
        setCountriesLoadError(true)
      }
    )
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY || step !== 7) return
    setDraft((prev) => {
      const rows = [...(prev.ownership?.shareholderRows ?? [])]
      if (!rows.some((r) => r.role !== 'beneficial_owner')) return prev
      return {
        ...prev,
        ownership: { ...(prev.ownership ?? {}), shareholderRows: clampShareholdersToBeneficialOwnerOnly(rows) },
      }
    })
  }, [step])

  const countryOptions = useMemo(
    () => referenceCountries.filter((c) => c.isActive !== false).sort((a, b) => a.name.localeCompare(b.name)),
    [referenceCountries]
  )

  const toggleFundsCorridorCountry = (fieldKey: 'incomingCountries' | 'outgoingCountries', countryName: string) => {
    setDraft((prev) => {
      const e = prev.entity ?? {}
      const cur = new Set(parseStoredCountryNames(String(e[fieldKey] ?? '')))
      if (cur.has(countryName)) cur.delete(countryName)
      else cur.add(countryName)
      const sorted = Array.from(cur).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      return { ...prev, entity: { ...e, [fieldKey]: joinStoredCountryNames(sorted) } }
    })
  }

  const setFundsCorridorFreeText = (fieldKey: 'incomingCountries' | 'outgoingCountries', text: string) => {
    setDraft((prev) => ({
      ...prev,
      entity: { ...(prev.entity ?? {}), [fieldKey]: text },
    }))
  }
  const persons = useMemo(() => portalDetails?.persons ?? [], [portalDetails])
  const documents = useMemo(() => portalDetails?.documents ?? [], [portalDetails])
  const directors = useMemo(
    () => persons.filter((p) => p.role.toUpperCase() === OnboardingPersonRoleCodes.Director),
    [persons]
  )
  const ubos = useMemo(() => persons.filter((p) => p.role.toUpperCase() === OnboardingPersonRoleCodes.Ubo), [persons])
  const hasDirectorInList = directors.length > 0

  const canSubmitLocally = useMemo(() => {
    const entityBlk = draft.entity ?? {}
    return (
      !!String(entityBlk.legalEntityName ?? '').trim() &&
      !!String(entityBlk.registrationNumber ?? '').trim() &&
      hasDirectorInList &&
      ubos.length > 0 &&
      documents.some((d) => docMatchesCode(d, OnboardingDocTypeCodes.CertificateOfIncorporation)) &&
      hasBusinessLevelAddressProof(documents) &&
      documents.some((d) => docMatchesCode(d, OnboardingDocTypeCodes.ArticlesOfAssociation)) &&
      directors.every(
        (dir) =>
          hasPersonScopedDoc(documents, dir.id, OnboardingDocTypeCodes.DirectorId) &&
          hasPersonScopedDoc(documents, dir.id, OnboardingDocTypeCodes.AddressProofDirector)
      ) &&
      ubos.every(
        (ubo) =>
          hasPersonScopedDoc(documents, ubo.id, OnboardingDocTypeCodes.UboId) &&
          hasPersonScopedDoc(documents, ubo.id, OnboardingDocTypeCodes.AddressProofShareholder)
      )
    )
  }, [draft.entity, directors, ubos, documents, hasDirectorInList])

  const uploadTypedDocumentFile = useCallback(
    async (documentType: string, personIdForScoped: string | undefined, file: File) => {
      if (!applicationId) {
        setMsg('Application not ready.')
        return
      }
      if (!auditUserId) {
        setMsg('Sign in again to continue.')
        return
      }
      if (file.size > FILE_MAX_BYTES) {
        setMsg('This file exceeds the upload limit (28 MB).')
        return
      }
      if (!ALLOW_EXT.test(file.name)) {
        setMsg('Allowed types: PDF, JPG, JPEG, PNG.')
        return
      }

      const dtNorm = documentType.trim().toUpperCase()
      const pidRaw = personIdForScoped?.trim()
      let personForUpload: string | undefined =
        pidRaw && pidRaw.length > 0 ? pidRaw : undefined

      if (
        dtNorm === OnboardingDocTypeCodes.DirectorId.toUpperCase() ||
        dtNorm === OnboardingDocTypeCodes.AddressProofDirector.toUpperCase()
      ) {
        if (!personForUpload || !directors.some((d) => d.id === personForUpload)) {
          setMsg('Director not found — finish the Directors step first.')
          return
        }
      } else if (
        dtNorm === OnboardingDocTypeCodes.UboId.toUpperCase() ||
        dtNorm === OnboardingDocTypeCodes.AddressProofShareholder.toUpperCase()
      ) {
        if (!personForUpload || !ubos.some((u) => u.id === personForUpload)) {
          setMsg('Beneficial owner not found — finish the Shareholders step first.')
          return
        }
      } else {
        personForUpload = undefined
      }

      const docTrim = documentType.trim()
      setBusy(true)
      setMsg(null)
      try {
        await onboardingApi.uploadDocument({
          applicationId,
          documentType: docTrim,
          personId: personForUpload,
          file,
          auditUserId,
        })
        await refreshApplication()
        setReloadToken((x) => x + 1)
      } catch (e: unknown) {
        setMsg(getErrorMessage(e))
      } finally {
        setBusy(false)
      }
    },
    [applicationId, auditUserId, directors, ubos, refreshApplication]
  )

  const persist = async (next: BusinessDraftV1): Promise<boolean> => {
    if (!applicationId || !auditUserId) return false
    setBusy(true)
    setMsg(null)
    try {
      await onboardingApi.saveExtendedQuestionnaire(applicationId, JSON.stringify(next), auditUserId)
      setDraft(next)
      return true
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  /** Creates/updates `Onboarding` person rows from structured shareholders / extra directors (minimal API fields). */
  const syncStakeholderPersonRecords = useCallback(
    async (base: BusinessDraftV1): Promise<BusinessDraftV1 | null> => {
      if (!applicationId || !auditUserId) {
        setMsg('Application not ready.')
        return null
      }
      const own = base.ownership ?? {}
      const shr = (own.shareholderRows ?? []).map((r) => ({
        ...r,
        syncedLinks: { ...(r.syncedLinks ?? {}) },
      }))
      const extra = (own.extraDirectorRows ?? []).map((r) => ({
        ...r,
        syncedLinks: { ...(r.syncedLinks ?? {}) },
      }))

      const upsertLink = async (row: IndividualStakeholderRow, link: 'director' | 'ubo', backendRole: string) => {
        const api = formatPersonApiFromStakeholder(row)
        const body = {
          role: backendRole,
          fullName: api.fullName,
          nationality: api.nationality,
          idNumber: api.idNumber,
        }

        const tryUpdateOrDropStale = async (personId: string): Promise<boolean> => {
          try {
            await onboardingApi.updatePerson(personId, body, auditUserId)
            return true
          } catch (e: unknown) {
            if (axios.isAxiosError(e) && e.response?.status === 404) {
              const remaining = { ...(row.syncedLinks ?? {}) }
              delete remaining[link]
              row.syncedLinks = Object.keys(remaining).length > 0 ? remaining : undefined
              return false
            }
            throw e
          }
        }

        let personId = row.syncedLinks?.[link]
        if (personId) {
          const ok = await tryUpdateOrDropStale(personId)
          if (ok) return
        }
        personId = await onboardingApi.addPerson(applicationId, body, auditUserId)
        row.syncedLinks = { ...(row.syncedLinks ?? {}), [link]: personId }
      }

      try {
        const ownSel = base.ownership ?? {}
        for (const row of shr) {
          if (row.role === 'beneficial_owner' || row.role === 'beneficial_owner_and_director')
            await upsertLink(row, 'ubo', OnboardingPersonRoleCodes.Ubo)
        }
        for (const row of shr) {
          if (shareholderRowCountsAsDirector(row, ownSel)) await upsertLink(row, 'director', OnboardingPersonRoleCodes.Director)
        }
        for (const row of extra) await upsertLink(row, 'director', OnboardingPersonRoleCodes.Director)
      } catch (e: unknown) {
        setMsg(getErrorMessage(e))
        return null
      }

      return {
        ...base,
        ownership: { ...own, shareholderRows: shr, extraDirectorRows: extra },
      }
    },
    [applicationId, auditUserId]
  )

  /** Maps extended questionnaire entity block into legacy `company_info` required for API submit validation. */
  const syncCompanyFromDraft = async (d: BusinessDraftV1): Promise<boolean> => {
    if (!applicationId || !auditUserId) return false
    const e = d.entity ?? {}
    const legal = String(e.legalEntityName ?? '').trim()
    const reg = String(e.registrationNumber ?? '').trim()
    const country = String(e.incorporationCountry ?? '').trim()
    const mainInd = String(e.mainIndustry ?? '').trim()
    const addInd = String(e.additionalIndustry ?? '').trim()
    const desc = String(e.productsDescription ?? '').trim()

    const businessActivityParts = [mainInd, addInd, desc.slice(0, 400)].filter(Boolean)
    const businessActivity =
      businessActivityParts.length > 0 ? businessActivityParts.join(' — ') : 'Business application (extended questionnaire)'
    try {
      await onboardingApi.upsertCompany(
        applicationId,
        {
          companyName: legal || 'Pending',
          registrationNumber: reg || '—',
          countryOfIncorporation: country || '—',
          businessActivity: businessActivity.slice(0, 4000),
          website: String(e.website ?? '').trim() || null,
        },
        auditUserId
      )
      await refreshApplication()
      return true
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
      return false
    }
  }

  const toggleInterest = (label: string) => {
    const p = draft.profile ?? { interests: [] }
    const cur = new Set(p.interests ?? [])
    if (cur.has(label)) cur.delete(label)
    else cur.add(label)
    setDraft({ ...draft, profile: { ...p, interests: Array.from(cur) } })
  }

  const goNext = async () => {
    setMsg(null)
    const profile = draft.profile ?? {}
    const entity = draft.entity ?? {}
    const ownership = draft.ownership ?? {}

    if (step === 0 && !profile.employeeBand?.trim()) {
      setMsg('Please select company size.')
      return
    }
    if (step === 1 && !profile.monthlyUsdVolumeBand?.trim()) {
      setMsg('Please select expected monthly volume (USD).')
      return
    }
    if (step === 2 && !profile.incomingTxBand?.trim()) {
      setMsg('Please select incoming transactions per month.')
      return
    }
    if (step === 3 && !profile.outgoingTxBand?.trim()) {
      setMsg('Please select outgoing transactions per month.')
      return
    }
    if (step === 4 && (!(profile.interests?.length ?? 0) || !profile.interests?.some((x) => x.trim()))) {
      setMsg('Select at least one area of interest.')
      return
    }
    if (step === 5) {
      if (
        !String(entity.incorporationCountry ?? '').trim() ||
        !String(entity.legalEntityName ?? '').trim() ||
        !String(entity.registrationNumber ?? '').trim()
      ) {
        setMsg('Country of incorporation, legal entity name, and registration number are required.')
        return
      }
      if (
        !entity.officeSameAsReg &&
        !String(entity.officeAddressLine ?? '').trim()
      ) {
        setMsg('Enter the office address, or tick “Office matches registered address” if they are the same.')
        return
      }
    }
    if (step === 6) {
      if (parseStoredCountryNames(String(entity.incomingCountries ?? '')).length === 0) {
        setMsg('Select at least one country for incoming funds.')
        return
      }
      if (parseStoredCountryNames(String(entity.outgoingCountries ?? '')).length === 0) {
        setMsg('Select at least one country for outgoing funds.')
        return
      }
      const incomeSrc = String(entity.incomeSource ?? '').trim()
      if (!incomeSrc) {
        setMsg('Select the primary source of income / wealth.')
        return
      }
      if (incomeSrc === 'other' && !String(entity.incomeSourceOther ?? '').trim()) {
        setMsg('Please specify details for “Other”.')
        return
      }
      const desc = String(entity.productsDescription ?? '').trim()
      if (desc.length < 50) {
        setMsg('Products / services must contain at least 50 characters.')
        return
      }
      const okSync = await syncCompanyFromDraft(draft)
      if (!okSync) return
      const persisted = await persist(draft)
      if (!persisted) return
      setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1))
      void refreshApplication()
      setReloadToken((x) => x + 1)
      return
    }
    if (step === 7) {
      const rows = ownership.shareholderRows ?? []
      for (let i = 0; i < rows.length; i++) {
        const err = validateIndividualStakeholder(rows[i], { requirePercent: true })
        if (err) {
          setMsg(`Shareholder ${i + 1}: ${err}`)
          return
        }
      }
      if (shouldValidateShareholdersTotal100(rows) && !isShareholderOwnershipTotalValid(rows)) {
        queueMicrotask(() => {
          ownershipTotalBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          ownershipTotalBannerRef.current?.focus()
        })
        return
      }
      const ntp = ownership.nomineeThirdPartyAnswer
      if (ntp !== 'yes' && ntp !== 'no') {
        setMsg(
          'Answer whether any business owners hold shares as a nominee or act on behalf of a third party (Yes / No).'
        )
        return
      }
      if (ntp === 'yes' && !String(ownership.nomineeQuestion ?? '').trim()) {
        setMsg('Briefly describe the nominee or third‑party arrangement.')
        return
      }
      const adr = ownership.shareholdersAreDirectors
      if (adr !== 'yes' && adr !== 'no') {
        setMsg('Answer whether your shareholders are also the company directors before continuing.')
        return
      }
      if (adr === 'yes') {
        const cov = ownership.shareholdersDirectorCoverage ?? ''
        if (cov !== 'all' && cov !== 'some') {
          setMsg('Specify whether every shareholder above is a director, or only some of them.')
          return
        }
        if (cov === 'some' && !(rows.some((r) => r.alsoDirector === true))) {
          setMsg('Select at least one shareholder who is a company director.')
          return
        }
      }
      const persisted = await persist(draft)
      if (!persisted) return
      setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1))
      void refreshApplication()
      setReloadToken((x) => x + 1)
      return
    }
    if (step === 8) {
      const shr = ownership.shareholderRows ?? []
      const extra = ownership.extraDirectorRows ?? []
      const directorLikeCount =
        shr.filter((r) => shareholderRowCountsAsDirector(r, ownership)).length + extra.length
      if (directorLikeCount < 1) {
        setMsg('Add at least one director (from shareholders or “Add another director”).')
        return
      }
      for (let i = 0; i < extra.length; i++) {
        const err = validateIndividualStakeholder(extra[i], { requirePercent: false })
        if (err) {
          setMsg(`Additional director ${i + 1}: ${err}`)
          return
        }
      }

      const merged = await syncStakeholderPersonRecords(draft)
      if (!merged) return
      const persisted = await persist(merged)
      if (!persisted) return
      try {
        const details = await onboardingApi.getDetails(applicationId!)
        const hasDir = details.persons.some((p) => p.role.toUpperCase() === OnboardingPersonRoleCodes.Director)
        if (!hasDir) {
          setMsg('Director could not be confirmed with the server. Please try again.')
          return
        }
      } catch (e: unknown) {
        setMsg(getErrorMessage(e))
        return
      }
      setStep(9)
      void refreshApplication()
      setReloadToken((x) => x + 1)
      return
    }

    const persisted = await persist(draft)
    if (!persisted) return

    setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1))

    void refreshApplication()
    setReloadToken((x) => x + 1)
  }

  const goPrev = () => setStep((s) => Math.max(s - 1, 0))

  const removePerson = async (personId: string) => {
    setBusy(true)
    setMsg(null)
    try {
      await onboardingApi.deletePerson(personId)
      await refreshApplication()
      setReloadToken((x) => x + 1)
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const removeDoc = async (documentId: string) => {
    setBusy(true)
    setMsg(null)
    try {
      await onboardingApi.deleteDocument(documentId)
      await refreshApplication()
      setReloadToken((x) => x + 1)
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const downloadDocFile = async (doc: ClientOnboardingDocumentDto) => {
    setDownloadBusyId(doc.id)
    setMsg(null)
    try {
      const fb =
        (doc.originalFileName ?? '').trim() ||
        (doc.documentType ? `${doc.documentType.replace(/_/g, '-')}.bin` : 'document')
      await onboardingApi.downloadDocument(doc.id, fb)
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
    } finally {
      setDownloadBusyId(null)
    }
  }

  const finishSubmit = async () => {
    if (!applicationId) return false
    setBusy(true)
    setMsg(null)
    try {
      const clientUid = portalDetails?.application?.userId
      if (!clientUid) {
        setMsg('Portal client user id missing — cannot submit.')
        return false
      }
      if (!(await syncCompanyFromDraft(draft))) return false
      await onboardingApi.submit(applicationId, clientUid, null, auditUserId)
      await refreshApplication()
      return true
    } catch (e: unknown) {
      setMsg(getErrorMessage(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  if (!adminUser?.id) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50/70 px-6 py-5 text-[#28293C]">
        <p className="font-semibold">Session missing</p>
        <Link to="/login" className="mt-3 inline-block text-sm font-semibold text-[#0C183B] underline">
          Sign in again
        </Link>
      </div>
    )
  }

  if (pageLoading) {
    return (
      <div className="rounded-2xl border border-[#C1C7D3]/60 bg-[#F8F9FB] px-8 py-12 text-center text-sm text-[#627396]">
        Loading business onboarding…
      </div>
    )
  }

  if (pageError || !application) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/70 px-6 py-5 text-sm text-[#28293C]">
        <p className="font-semibold">{pageError ?? 'Application not available.'}</p>
        <Link
          to={routeApplicationId ? `/onboarding/${routeApplicationId}` : '/onboarding'}
          className="mt-3 inline-block text-sm font-semibold text-[#0C183B] underline"
        >
          Back to application
        </Link>
      </div>
    )
  }

  if (!isBusiness) {
    return <Navigate to={routeApplicationId ? `/onboarding/${routeApplicationId}` : '/onboarding'} replace />
  }

  const profile = draft.profile ?? {}
  const entity = draft.entity ?? {}
  const ownership = draft.ownership ?? {}
  const lastStepIndex = STEP_LABELS.length - 1

  const patchShareholderRow = (ix: number, patch: Partial<IndividualStakeholderRow>) => {
    setDraft((prev) => {
      const o = prev.ownership ?? {}
      const rows = [...(o.shareholderRows ?? [])]
      const prevRow = rows[ix]
      if (!prevRow) return prev
      const nextPatch = { ...patch }
      if (patch.role !== undefined && patch.role !== prevRow.role) nextPatch.syncedLinks = undefined
      let mergedRow: IndividualStakeholderRow = { ...prevRow, ...nextPatch }
      if (SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY && mergedRow.role !== 'beneficial_owner')
        mergedRow = { ...mergedRow, role: 'beneficial_owner', syncedLinks: undefined }
      rows[ix] = mergedRow
      return { ...prev, ownership: { ...o, shareholderRows: rows } }
    })
  }

  const patchExtraDirectorRow = (ix: number, patch: Partial<IndividualStakeholderRow>) => {
    setDraft((prev) => {
      const o = prev.ownership ?? {}
      const rows = [...(o.extraDirectorRows ?? [])]
      const prevRow = rows[ix]
      if (!prevRow) return prev
      const nextPatch = { ...patch }
      if (patch.role !== undefined && patch.role !== prevRow.role) nextPatch.syncedLinks = undefined
      rows[ix] = { ...prevRow, ...nextPatch }
      return { ...prev, ownership: { ...o, extraDirectorRows: rows } }
    })
  }

  function renderEntityFieldRow(fieldKey: string, label: string) {
    const value = String(entity[fieldKey] ?? '')
    if (ENTITY_COUNTRY_SELECT_KEYS.has(fieldKey)) {
      const inCatalogue = countryOptions.some((c) => c.name === value)
      if (countriesLoadError && referenceCountries.length === 0) {
        return (
          <label key={fieldKey} className="block text-sm">
            <span className="text-[#627396]">{label}</span>
            <input
              className="mt-1 w-full rounded-lg border px-3 py-2"
              style={{ borderColor: '#C1C7D3' }}
              value={value}
              onChange={(e) =>
                setDraft({ ...draft, entity: { ...entity, [fieldKey]: e.target.value } })
              }
              placeholder="Could not load country list — type country name"
            />
          </label>
        )
      }
      return (
        <label key={fieldKey} className="block text-sm">
          <span className="text-[#627396]">{label}</span>
          <select
            className="mt-1 w-full rounded-lg border px-3 py-2 bg-white"
            style={{ borderColor: '#C1C7D3' }}
            value={value}
            disabled={referenceCountries.length === 0 && !countriesLoadError}
            onChange={(e) =>
              setDraft({
                ...draft,
                entity: { ...entity, [fieldKey]: e.target.value },
              })
            }
          >
            <option value="">
              {referenceCountries.length === 0 && !countriesLoadError ? 'Loading countries…' : 'Select country…'}
            </option>
            {value && !inCatalogue ? (
              <option value={value}>
                {value} (saved)
              </option>
            ) : null}
            {countryOptions.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )
    }
    return (
      <label key={fieldKey} className="block text-sm">
        <span className="text-[#627396]">{label}</span>
        <input
          className="mt-1 w-full rounded-lg border px-3 py-2"
          style={{ borderColor: '#C1C7D3' }}
          value={value}
          onChange={(e) => setDraft({ ...draft, entity: { ...entity, [fieldKey]: e.target.value } })}
        />
      </label>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-16">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-[#627396]">Business onboarding</p>
          <h1 className="mt-1 text-2xl font-semibold text-[#28293C]" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Application questionnaire
          </h1>
          <p className="mt-2 max-w-xl text-sm text-[#627396]">
            Step through profile questions, entity and business descriptions, shareholders and directors, then documents and
            review — all in order.
          </p>
        </div>
        <Link
          to={`/onboarding/${application.id}`}
          className="shrink-0 rounded-lg border border-[#C1C7D3] bg-white px-4 py-2 text-sm font-semibold hover:bg-[#F1F4F8]"
        >
          Back to application
        </Link>
      </header>

      <nav aria-label="Wizard steps" className="flex flex-wrap gap-2 border-b border-[#E3E8EF] pb-3">
        {STEP_LABELS.map((label, i) => {
          const active = i === step
          return (
            <button
              key={label}
              type="button"
              disabled={busy}
              title={`${i + 1}. ${label}`}
              onClick={() => setStep(i)}
              className="max-w-[12rem] truncate rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50"
              style={{
                background: active ? '#25325A' : '#F1F4F8',
                color: active ? '#FFFFFF' : '#627396',
              }}
            >
              {i + 1}. {label}
            </button>
          )
        })}
      </nav>

      {/* 0–4 introductory questions */}
      {step === 0 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <p className="text-xs uppercase tracking-wide text-[#627396]">Question 1 of 5 · Profile</p>
          <h2 className="text-lg font-semibold text-[#0C183B]">How big is your company?</h2>
          <label className="block text-sm">
            <span className="text-[#627396]">Employees</span>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2"
              style={{ borderColor: '#C1C7D3' }}
              value={profile.employeeBand ?? ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  profile: { ...profile, employeeBand: e.target.value },
                })
              }
            >
              <option value="">Select…</option>
              {EMPLOYEE_BANDS.map((x) => (
                <option key={x} value={x}>
                  {x} employees
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <p className="text-xs uppercase tracking-wide text-[#627396]">Question 2 of 5 · Expected activity</p>
          <h2 className="text-lg font-semibold text-[#0C183B]">Expected monthly volume with XChangeLab</h2>
          <label className="block text-sm">
            <span className="text-[#627396]">USD</span>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2"
              style={{ borderColor: '#C1C7D3' }}
              value={profile.monthlyUsdVolumeBand ?? ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  profile: { ...profile, monthlyUsdVolumeBand: e.target.value },
                })
              }
            >
              <option value="">Select…</option>
              {USD_VOLUME_BANDS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <p className="text-xs uppercase tracking-wide text-[#627396]">Question 3 of 5 · Transactions</p>
          <h2 className="text-lg font-semibold text-[#0C183B]">Incoming transactions per month</h2>
          <label className="block text-sm">
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2"
              style={{ borderColor: '#C1C7D3' }}
              value={profile.incomingTxBand ?? ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  profile: { ...profile, incomingTxBand: e.target.value },
                })
              }
            >
              <option value="">Select…</option>
              {TX_BANDS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {step === 3 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <p className="text-xs uppercase tracking-wide text-[#627396]">Question 4 of 5 · Transactions</p>
          <h2 className="text-lg font-semibold text-[#0C183B]">Outgoing transactions per month</h2>
          <label className="block text-sm">
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2"
              style={{ borderColor: '#C1C7D3' }}
              value={profile.outgoingTxBand ?? ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  profile: { ...profile, outgoingTxBand: e.target.value },
                })
              }
            >
              <option value="">Select…</option>
              {TX_BANDS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {step === 4 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <p className="text-xs uppercase tracking-wide text-[#627396]">Question 5 of 5 · Interests</p>
          <h2 className="text-lg font-semibold text-[#0C183B]">Primary areas of interest</h2>
          <ul className="max-h-60 space-y-2 overflow-auto text-sm">
            {INTERESTS.map((t) => (
              <li key={t}>
                <label className="flex cursor-pointer gap-2">
                  <input
                    type="checkbox"
                    checked={(profile.interests ?? []).includes(t)}
                    onChange={() => toggleInterest(t)}
                  />
                  <span>{t}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      {step === 5 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <h2 className="text-lg font-semibold text-[#0C183B]">Entity information</h2>
          <p className="text-sm text-[#627396]">
            Legal identity and registration. Business narrative and counterparties are captured on the next step.
          </p>
          {ENTITY_FIELD_KEYS_HEAD_LEAD.map(([key, label]) => renderEntityFieldRow(key, label))}
          <label className="block text-sm">
            <span className="text-[#627396]">Entity type</span>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-white"
              style={{ borderColor: '#C1C7D3' }}
              value={String(entity.entityType ?? 'limited_liability_company')}
              onChange={(e) => setDraft({ ...draft, entity: { ...entity, entityType: e.target.value } })}
            >
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {entityTypeDisplayLabel(t)}
                </option>
              ))}
            </select>
          </label>
          {String(entity.entityType) === 'other' ? (
            <label className="block text-sm">
              <span className="text-[#627396]">Specify entity type</span>
              <input
                className="mt-1 w-full rounded-lg border px-3 py-2"
                style={{ borderColor: '#C1C7D3' }}
                value={String(entity.entityTypeOther ?? '')}
                onChange={(e) => setDraft({ ...draft, entity: { ...entity, entityTypeOther: e.target.value } })}
              />
            </label>
          ) : null}
          {ENTITY_FIELD_KEYS_HEAD_REST.map(([key, label]) => renderEntityFieldRow(key, label))}
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={Boolean(entity.officeSameAsReg)}
              onChange={(e) => {
                const checked = e.target.checked
                setDraft({
                  ...draft,
                  entity: {
                    ...entity,
                    officeSameAsReg: checked,
                    ...(checked ? { officeAddressLine: '' } : {}),
                  },
                })
              }}
            />
            <span>
              Office address matches registered address (same street, city, postal code, and country). You do not need
              to fill a separate office line.
            </span>
          </label>
          {!entity.officeSameAsReg ? (
            <label className="block text-sm">
              <span className="text-[#627396]">Office address — street (and notes if city/country differ)</span>
              <input
                className="mt-1 w-full rounded-lg border px-3 py-2"
                style={{ borderColor: '#C1C7D3' }}
                value={String(entity.officeAddressLine ?? '')}
                onChange={(e) =>
                  setDraft({ ...draft, entity: { ...entity, officeAddressLine: e.target.value } })
                }
                placeholder="Principal place of business if different from registered office above"
              />
            </label>
          ) : null}
          {ENTITY_FIELD_KEYS_TAIL.map(([key, label]) => renderEntityFieldRow(key, label))}
        </section>
      )}

      {step === 6 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <h2 className="text-lg font-semibold text-[#0C183B]">Business description</h2>
          <p className="text-sm text-[#627396]">Operational description, corridors, and source of wealth.</p>
          {FUNDS_CORRIDOR_FIELDS.map(({ key, label }) => {
            const stored = parseStoredCountryNames(String(entity[key] ?? ''))
            const selectedSet = new Set(stored)
            const catalogueNameSet = new Set(countryOptions.map((c) => c.name))

            return (
              <div key={key} className="space-y-2">
                <span className="block text-sm text-[#627396]">{label} <span className="text-red-600">*</span></span>
                {countriesLoadError && referenceCountries.length === 0 ? (
                  <input
                    className="mt-1 w-full rounded-lg border px-3 py-2"
                    style={{ borderColor: '#C1C7D3' }}
                    value={String(entity[key] ?? '')}
                    onChange={(e) => setFundsCorridorFreeText(key, e.target.value)}
                  />
                ) : referenceCountries.length === 0 ? (
                  <p className="text-sm italic text-[#627396]">Loading countries…</p>
                ) : (
                  <div className="space-y-2">
                    <div
                      className="flex min-h-[2.75rem] flex-wrap gap-2 rounded-lg border bg-[#F8F9FB] p-3"
                      style={{ borderColor: '#C1C7D3' }}
                    >
                      {stored.length === 0 ? (
                        <span className="self-center text-sm text-[#627396]">No countries yet — add below.</span>
                      ) : (
                        stored.map((name) => (
                          <span
                            key={`${key}:${name}`}
                            className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-white pl-2.5 pr-1 py-0.5 text-sm font-medium text-[#28293C] shadow-sm"
                            style={{ borderColor: '#D5DAE3' }}
                          >
                            <span className="truncate">{name}</span>
                            {!catalogueNameSet.has(name) ? (
                              <span className="shrink-0 text-[10px] font-normal uppercase tracking-wide text-[#627396]">
                                saved
                              </span>
                            ) : null}
                            <button
                              type="button"
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#627396] hover:bg-[#EEF1F6] hover:text-[#28293C]"
                              aria-label={`Remove ${name}`}
                              onClick={() => toggleFundsCorridorCountry(key, name)}
                            >
                              ×
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                    <select
                      className="w-full rounded-lg border bg-white px-3 py-2 text-sm"
                      style={{ borderColor: '#C1C7D3' }}
                      value={fundsCountryPicker[key]}
                      onChange={(e) => {
                        const v = e.target.value
                        if (!v) return
                        toggleFundsCorridorCountry(key, v)
                        setFundsCountryPicker((prev) => ({ ...prev, [key]: '' }))
                      }}
                    >
                      <option value="">Add country…</option>
                      {countryOptions
                        .filter((c) => !selectedSet.has(c.name))
                        .map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
            )
          })}
          <label className="block text-sm">
            <span className="text-[#627396]">Primary source of income / wealth <span className="text-red-600">*</span></span>
            <select
              required
              className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
              style={{ borderColor: '#C1C7D3' }}
              value={String(entity.incomeSource ?? '').trim()}
              onChange={(e) => {
                const v = e.target.value
                setDraft({
                  ...draft,
                  entity: {
                    ...entity,
                    incomeSource: v,
                    ...(v !== 'other' ? { incomeSourceOther: '' } : {}),
                  },
                })
              }}
            >
              <option value="">Select primary source…</option>
              {!INCOME_SOURCE_VALUES.has(String(entity.incomeSource ?? '').trim()) &&
              String(entity.incomeSource ?? '').trim() ? (
                <option value={String(entity.incomeSource ?? '').trim()}>
                  {String(entity.incomeSource ?? '').trim()} (previous value)
                </option>
              ) : null}
              {INCOME_SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {String(entity.incomeSource ?? '').trim() === 'other' ? (
            <label className="block text-sm">
              <span className="text-[#627396]">Specify income source</span>
              <input
                className="mt-1 w-full rounded-lg border px-3 py-2"
                style={{ borderColor: '#C1C7D3' }}
                value={String(entity.incomeSourceOther ?? '')}
                onChange={(e) =>
                  setDraft({ ...draft, entity: { ...entity, incomeSourceOther: e.target.value } })
                }
                placeholder="Describe where income / wealth originates"
              />
            </label>
          ) : null}
          <label className="block text-sm">
            <span className="text-[#627396]">
              Products / services <span className="text-red-600">*</span> (at least 50 characters)
            </span>
            <textarea
              required
              minLength={50}
              rows={6}
              className="mt-1 w-full rounded-lg border px-3 py-2"
              style={{ borderColor: '#C1C7D3' }}
              value={String(entity.productsDescription ?? '')}
              onChange={(e) => setDraft({ ...draft, entity: { ...entity, productsDescription: e.target.value } })}
              placeholder={
                'Example: We run a specialty coffee roasting business. We sell roasted coffee beans online through our own website and wholesale to local cafes. Our customers include individual coffee enthusiasts and small coffee shops.'
              }
            />
          </label>
        </section>
      )}

      {step === 7 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <h2 className="text-lg font-semibold text-[#0C183B]">Shareholders</h2>
          {shouldValidateShareholdersTotal100(ownership.shareholderRows ?? []) ? (
            <p
              ref={ownershipTotalBannerRef}
              tabIndex={-1}
              className={`rounded-lg border px-3 py-2.5 text-sm leading-snug outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-amber-400 ${
                (() => {
                  const t = shareholderPercentTotal(ownership.shareholderRows ?? [])
                  return t !== null && Math.abs(t - 100) <= 0.01
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                    : 'border-amber-300 bg-amber-50 text-amber-950'
                })()
              }`}
            >
              {(() => {
                const shr = ownership.shareholderRows ?? []
                const total = shareholderPercentTotal(shr)
                if (total === null)
                  return 'Ownership total: finish entering a valid % (0–100) for each shareholder to see whether the allocation adds up to 100%.'
                if (Math.abs(total - 100) <= 0.01)
                  return (
                    <>
                      Ownership total is <strong>100%</strong> — allocations look complete.
                    </>
                  )
                return (
                  <>
                    Ownership total is <strong>{total}%</strong> — it must equal <strong>100%</strong> before you can continue.
                  </>
                )
              })()}
            </p>
          ) : null}
          {(ownership.shareholderRows ?? []).map((row, ix) => (
            <div
              key={ix}
              className="space-y-3 rounded-xl border px-4 py-4 shadow-sm"
              style={{ borderColor: '#E3E8EF', background: '#FAFBFC' }}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#627396]">
                  Shareholder {ix + 1}
                </span>
                {(ownership.shareholderRows ?? []).length > 1 ? (
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs font-semibold text-[#991B1B] hover:underline disabled:opacity-50"
                    onClick={() =>
                      setDraft((prev) => {
                        const o = prev.ownership ?? {}
                        const rows = [...(o.shareholderRows ?? [])].filter((_, j) => j !== ix)
                        return { ...prev, ownership: { ...o, shareholderRows: rows.length ? rows : [emptyIndividualStakeholder()] } }
                      })
                    }
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <IndividualStakeholderFormFields
                variant="shareholder"
                busy={busy}
                row={row}
                countryOptions={countryOptions}
                referenceCountries={referenceCountries}
                countriesLoadError={countriesLoadError}
                onPatch={(patch) => patchShareholderRow(ix, patch)}
              />
            </div>
          ))}
          <button
            type="button"
            disabled={busy}
            className="rounded-lg border border-[#C1C7D3] bg-white px-4 py-2 text-sm font-semibold text-[#25325A] hover:bg-[#F1F4F8] disabled:opacity-50"
            onClick={() =>
              setDraft((prev) => {
                const o = prev.ownership ?? {}
                const rows = [...(o.shareholderRows ?? [])]
                rows.push(emptyIndividualStakeholder())
                return { ...prev, ownership: { ...o, shareholderRows: rows } }
              })
            }
          >
            Add another shareholder
          </button>
          <div className="space-y-3">
            <p className="text-sm font-medium text-[#28293C]">
              Do any business owners hold shares or act on behalf of a third party?
            </p>
            <p className="text-xs leading-relaxed text-[#627396]">
              This includes holding shares as a nominee or conducting business activities for another party.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#28293C]">
                <input
                  type="radio"
                  name="nominee-third-party-q"
                  className="h-4 w-4 shrink-0"
                  disabled={busy}
                  checked={ownership.nomineeThirdPartyAnswer === 'yes'}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      ownership: { ...ownership, nomineeThirdPartyAnswer: 'yes' },
                    })
                  }
                />
                Yes
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#28293C]">
                <input
                  type="radio"
                  name="nominee-third-party-q"
                  className="h-4 w-4 shrink-0"
                  disabled={busy}
                  checked={ownership.nomineeThirdPartyAnswer === 'no'}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      ownership: {
                        ...ownership,
                        nomineeThirdPartyAnswer: 'no',
                        nomineeQuestion: '',
                      },
                    })
                  }
                />
                No
              </label>
            </div>
            {ownership.nomineeThirdPartyAnswer === 'yes' ? (
              <label className="mt-2 block text-sm">
                <span className="text-[#627396]">Provide details</span>
                <textarea
                  rows={4}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: '#C1C7D3' }}
                  disabled={busy}
                  value={ownership.nomineeQuestion ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, ownership: { ...ownership, nomineeQuestion: e.target.value } })
                  }
                  placeholder="Describe the arrangement (nominee holdings, acting for another party, etc.)."
                />
              </label>
            ) : null}
          </div>

          <div className="rounded-xl border px-4 py-4 shadow-sm" style={{ borderColor: '#E3E8EF', background: '#FAFBFC' }}>
            <p className="text-sm font-medium text-[#28293C]">Are your shareholders also the directors of this company?</p>
            <p className="mt-1 text-xs leading-relaxed text-[#627396]">
              Helps classify how shareholders relate to your director structure for compliance (identity verification via
              Didit may be wired in later).
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <label className="flex cursor-pointer items-start gap-2 text-sm text-[#28293C]">
                <input
                  type="radio"
                  name="shareholders-directors-q"
                  className="mt-1"
                  disabled={busy}
                  checked={ownership.shareholdersAreDirectors === 'yes'}
                  onChange={() =>
                    setDraft((prev) => {
                      const o = prev.ownership ?? {}
                      const wasNo = o.shareholdersAreDirectors === 'no'
                      return {
                        ...prev,
                        ownership: {
                          ...o,
                          shareholdersAreDirectors: 'yes',
                          shareholdersDirectorCoverage: wasNo ? '' : (o.shareholdersDirectorCoverage ?? ''),
                        },
                      }
                    })
                  }
                />
                <span>
                  <span className="font-medium">Yes</span>
                  <span className="mt-0.5 block text-xs font-normal text-[#627396]">
                    At least some shareholders listed above serve as directors.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-[#28293C]">
                <input
                  type="radio"
                  name="shareholders-directors-q"
                  className="mt-1"
                  disabled={busy}
                  checked={ownership.shareholdersAreDirectors === 'no'}
                  onChange={() =>
                    setDraft((prev) => {
                      const o = prev.ownership ?? {}
                      const rows = (o.shareholderRows ?? []).map((r) => ({ ...r, alsoDirector: false }))
                      return {
                        ...prev,
                        ownership: {
                          ...o,
                          shareholdersAreDirectors: 'no',
                          shareholdersDirectorCoverage: '',
                          shareholderRows: rows,
                        },
                      }
                    })
                  }
                />
                <span>
                  <span className="font-medium">No</span>
                  <span className="mt-0.5 block text-xs font-normal text-[#627396]">
                    Directors include people who are not treated as shareholders here.
                  </span>
                </span>
              </label>
            </div>

            {ownership.shareholdersAreDirectors === 'yes' ? (
              <div className="mt-6 space-y-4 border-t border-[#E3E8EF] pt-5">
                <p className="text-sm font-medium text-[#28293C]">
                  Which shareholders from the list above are company directors?
                </p>
                <p className="text-xs leading-relaxed text-[#627396]">
                  Mark <strong>all</strong>, or tick only selected people — for example when only some of your shareholders
                  are on the board.
                </p>
                <div className="flex flex-col gap-3">
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-[#28293C]">
                    <input
                      type="radio"
                      name="shareholders-director-scope"
                      className="mt-1"
                      disabled={busy}
                      checked={(ownership.shareholdersDirectorCoverage ?? '') === 'all'}
                      onChange={() =>
                        setDraft((prev) => {
                          const o = prev.ownership ?? {}
                          const rows = (o.shareholderRows ?? []).map((r) => ({ ...r, alsoDirector: false }))
                          return {
                            ...prev,
                            ownership: {
                              ...o,
                              shareholdersDirectorCoverage: 'all',
                              shareholderRows: rows,
                            },
                          }
                        })
                      }
                    />
                    <span>
                      <span className="font-medium">Everyone in the shareholder list above</span>
                      <span className="mt-0.5 block text-xs font-normal text-[#627396]">
                        All individuals you entered are directors of the company.
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-[#28293C]">
                    <input
                      type="radio"
                      name="shareholders-director-scope"
                      className="mt-1"
                      disabled={busy}
                      checked={(ownership.shareholdersDirectorCoverage ?? '') === 'some'}
                      onChange={() =>
                        setDraft((prev) => {
                          const o = prev.ownership ?? {}
                          const rows = (o.shareholderRows ?? []).map((r) => ({ ...r, alsoDirector: false }))
                          return {
                            ...prev,
                            ownership: {
                              ...o,
                              shareholdersDirectorCoverage: 'some',
                              shareholderRows: rows,
                            },
                          }
                        })
                      }
                    />
                    <span>
                      <span className="font-medium">Only some of them — choose below</span>
                      <span className="mt-0.5 block text-xs font-normal text-[#627396]">
                        Tick each shareholder who is also a director.
                      </span>
                    </span>
                  </label>
                </div>
                {(ownership.shareholdersDirectorCoverage ?? '') === 'some' ? (
                  <ul
                    className="overflow-hidden rounded-lg border divide-y divide-[#E3E8EF]"
                    style={{ borderColor: '#C1C7D3' }}
                  >
                    {(ownership.shareholderRows ?? []).map((row, ix) => (
                      <li
                        key={ix}
                        className="flex flex-wrap items-center justify-between gap-3 bg-white px-4 py-3 text-sm text-[#28293C]"
                      >
                        <div className="min-w-0">
                          <p className="font-semibold truncate">
                            {shareholderCompositeName(row) ||
                              `${STAKEHOLDER_ROLE_OPTIONS.find((o) => o.value === row.role)?.label ?? 'Shareholder'} ${ix + 1}`}
                          </p>
                          <p className="mt-0.5 text-xs text-[#627396]">Shareholder #{ix + 1}</p>
                        </div>
                        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm font-medium">
                          <input
                            type="checkbox"
                            disabled={busy}
                            className="h-4 w-4 rounded border-[#C1C7D3]"
                            checked={row.alsoDirector === true}
                            onChange={(e) => patchShareholderRow(ix, { alsoDirector: e.target.checked })}
                          />
                          Is a director
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      )}

      {step === 8 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <h2 className="text-lg font-semibold text-[#0C183B]">List of directors</h2>
          <p className="text-sm text-[#627396]">
            Shareholders who are company directors appear here according to what you chose in the shareholder step
            (“everyone” or individually). Open a row to edit the displayed name only. Use “Additional directors” if a
            director is not entered as shareholder.
          </p>

          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[#627396]">From shareholders</h3>
            {(ownership.shareholderRows ?? []).map((row, ix) =>
              shareholderRowCountsAsDirector(row, ownership) ? (
                <div
                  key={`bd-${ix}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 text-sm"
                  style={{ borderColor: '#C1C7D3' }}
                >
                  <div>
                    <p className="font-semibold text-[#28293C]">{shareholderCompositeName(row) || '—'}</p>
                    <p className="mt-1 text-xs text-[#627396]">
                      Linked shareholder ·{' '}
                      {STAKEHOLDER_ROLE_OPTIONS.find((o) => o.value === row.role)?.label ?? 'Beneficial owner'}
                      {(ownership.shareholdersDirectorCoverage ?? '') === 'all'
                        ? ' · All shareholders counted as directors'
                        : ' · Selected as director'}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-lg border border-[#C1C7D3] bg-[#F8F9FB] px-3 py-1.5 text-xs font-semibold text-[#25325A] hover:bg-[#EEF1F6] disabled:opacity-50"
                    onClick={() => {
                      setDirectorFioModalIx(ix)
                      setDirectorFioDraft({
                        firstName: row.firstName,
                        middleName: row.middleName,
                        lastName: row.lastName,
                      })
                    }}
                  >
                    View / edit name
                  </button>
                </div>
              ) : null
            )}
            {(ownership.shareholderRows ?? []).every((r) => !shareholderRowCountsAsDirector(r, ownership)) ? (
              <p className="text-sm text-[#627396]">No shareholder directors captured yet.</p>
            ) : null}
          </div>

          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[#627396]">Additional directors</h3>
            {(ownership.extraDirectorRows ?? []).map((row, ix) => (
              <div
                key={`exdir-${ix}`}
                className="space-y-3 rounded-xl border px-4 py-4 shadow-sm"
                style={{ borderColor: '#E3E8EF', background: '#FAFBFC' }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#627396]">
                    Director {ix + 1}
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs font-semibold text-[#991B1B] hover:underline disabled:opacity-50"
                    onClick={() =>
                      setDraft((prev) => {
                        const o = prev.ownership ?? {}
                        const rows = [...(o.extraDirectorRows ?? [])].filter((_, j) => j !== ix)
                        return { ...prev, ownership: { ...o, extraDirectorRows: rows } }
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
                <IndividualStakeholderFormFields
                  hidePercentShares
                  busy={busy}
                  row={{ ...row, role: row.role ?? 'director' }}
                  countryOptions={countryOptions}
                  referenceCountries={referenceCountries}
                  countriesLoadError={countriesLoadError}
                  onPatch={(patch) => patchExtraDirectorRow(ix, patch)}
                />
              </div>
            ))}
            <button
              type="button"
              disabled={busy}
              className="rounded-lg border border-[#C1C7D3] bg-white px-4 py-2 text-sm font-semibold text-[#25325A] hover:bg-[#F1F4F8] disabled:opacity-50"
              onClick={() =>
                setDraft((prev) => {
                  const o = prev.ownership ?? {}
                  const next = emptyIndividualStakeholder()
                  next.role = 'director'
                  const rows = [...(o.extraDirectorRows ?? [])]
                  rows.push(next)
                  return { ...prev, ownership: { ...o, extraDirectorRows: rows } }
                })
              }
            >
              Add another director
            </button>
          </div>

          {persons.length > 0 ? (
            <div className="rounded-lg border px-4 py-3 text-sm" style={{ borderColor: '#E3E8EF', background: '#FFFFFF' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#627396]">
                Persons already on file (backend)
              </h3>
              <ul className="mt-2 divide-y" style={{ borderColor: '#EEF1F6' }}>
                {persons.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-start justify-between gap-3 py-2">
                    <div>
                      <p className="font-medium text-[#28293C]">{p.fullName}</p>
                      <p className="text-xs text-[#627396]">
                        {p.role.replace(/_/g, ' ')} · {p.nationality} · {p.idNumber}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      className="text-xs font-semibold text-[#991B1B] hover:underline disabled:opacity-50"
                      onClick={() => void removePerson(p.id)}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      )}

      {step === 9 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <h2 className="text-lg font-semibold text-[#0C183B]">Documents</h2>
          <p className="text-sm text-[#627396]">
            Press <span className="font-medium text-[#28293C]">Upload</span> beside the requirement you are filling — the
            category is filled in for you. PDF, JPG or PNG · up to 28 MB per file.
          </p>

          <ul className="space-y-3">
            {BUSINESS_DOCUMENT_GUIDE.map((g) => {
              let satisfied = false
              let detail: string | null = null

              if (g.personRole === 'director') {
                satisfied =
                  directors.length > 0 &&
                  directors.every((dir) => hasPersonScopedDoc(documents, dir.id, g.code))
                detail =
                  directors.length === 0 ? 'Add directors first' : `${directors.length} director(s) on file`
              } else if (g.personRole === 'ubo') {
                satisfied =
                  ubos.length > 0 &&
                  ubos.every((ubo) => hasPersonScopedDoc(documents, ubo.id, g.code))
                detail = ubos.length === 0 ? 'Add shareholders first' : `${ubos.length} beneficial owner(s) on file`
              } else if (g.code === OnboardingDocTypeCodes.ProofOfBusinessAddress) {
                satisfied = hasBusinessLevelAddressProof(documents)
                detail = 'Company-level (not tied to a person)'
              } else if (g.required) {
                satisfied = documents.some((d) => docMatchesCode(d, g.code))
              } else {
                const n = documents.filter((d) => docMatchesCode(d, g.code)).length
                detail = n ? `${n} file(s)` : 'Optional'
              }

              const fileCountCategory = documents.filter((d) => docMatchesCode(d, g.code)).length

              let uploadsBlock: ReactNode = null
              if (!g.personRole) {
                uploadsBlock = (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-[#627396]">
                      {fileCountCategory === 0 ? 'No files yet' : `${fileCountCategory} file(s) in this category`}
                    </span>
                    <BusinessDocUploadButton
                      accept={DOC_FILE_ACCEPT}
                      disabled={busy || !applicationId}
                      onFile={(f) => void uploadTypedDocumentFile(g.code, undefined, f)}
                    />
                  </div>
                )
              } else if (g.personRole === 'director') {
                uploadsBlock = (
                  <ul className="mt-2 space-y-2 border-t border-[#EEF1F6] pt-2">
                    {directors.length === 0 ? (
                      <li className="text-xs text-[#92400E]">Add directors on the previous step.</li>
                    ) : (
                      directors.map((dir) => {
                        const ok = hasPersonScopedDoc(documents, dir.id, g.code)
                        return (
                          <li
                            key={dir.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#FAFBFC] px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#28293C]">{dir.fullName}</p>
                              <p className={`text-xs ${ok ? 'text-[#065F46]' : 'text-[#92400E]'}`}>
                                {ok ? 'Uploaded' : 'Awaiting upload'}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <BusinessDocUploadButton
                                accept={DOC_FILE_ACCEPT}
                                disabled={busy || !applicationId}
                                onFile={(f) => void uploadTypedDocumentFile(g.code, dir.id, f)}
                              />
                            </div>
                          </li>
                        )
                      })
                    )}
                  </ul>
                )
              } else {
                uploadsBlock = (
                  <ul className="mt-2 space-y-2 border-t border-[#EEF1F6] pt-2">
                    {ubos.length === 0 ? (
                      <li className="text-xs text-[#92400E]">Complete the Shareholders step first.</li>
                    ) : (
                      ubos.map((uboRow) => {
                        const ok = hasPersonScopedDoc(documents, uboRow.id, g.code)
                        return (
                          <li
                            key={uboRow.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#FAFBFC] px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#28293C]">{uboRow.fullName}</p>
                              <p className={`text-xs ${ok ? 'text-[#065F46]' : 'text-[#92400E]'}`}>
                                {ok ? 'Uploaded' : 'Awaiting upload'}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <BusinessDocUploadButton
                                accept={DOC_FILE_ACCEPT}
                                disabled={busy || !applicationId}
                                onFile={(f) => void uploadTypedDocumentFile(g.code, uboRow.id, f)}
                              />
                            </div>
                          </li>
                        )
                      })
                    )}
                  </ul>
                )
              }

              return (
                <li
                  key={g.code}
                  className="rounded-xl border px-4 py-3 text-sm"
                  style={{ borderColor: '#E3E8EF', background: '#FFFFFF', color: '#28293C' }}
                >
                  <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                    <span className={`shrink-0 font-semibold ${g.required ? 'text-[#0C183B]' : 'text-[#627396]'}`}>
                      {g.required ? '*' : '○'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="font-medium text-[#28293C]">
                          {g.label}
                          {!g.required ? (
                            <span className="ml-2 text-xs font-normal text-[#627396]">Optional</span>
                          ) : null}
                        </span>
                        <span
                          className={`text-xs font-semibold ${g.required ? (satisfied ? 'text-[#065F46]' : 'text-[#92400E]') : 'text-[#627396]'}`}
                        >
                          {g.required ? (satisfied ? 'Ready' : 'Missing') : detail ?? ''}
                        </span>
                      </div>
                      {g.hint ? <p className="mt-1 text-xs text-[#627396]">{g.hint}</p> : null}
                      {uploadsBlock}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[#627396]">Stored ({documents.length})</h3>
            {documents.length === 0 ? (
              <p className="mt-2 text-sm text-[#627396]">Nothing uploaded.</p>
            ) : (
              <ul className="mt-3 divide-y rounded-lg border" style={{ borderColor: '#C1C7D3' }}>
                {documents.map((doc) => (
                  <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#28293C]">
                        {(doc.originalFileName ?? '').trim() ||
                          doc.documentType?.replace(/_/g, ' ') ||
                          doc.id}
                      </p>
                      <p className="truncate text-xs text-[#627396]">
                        {documentTypeDisplayLabel(doc.documentType)}
                        {(() => {
                          const who = formatDocPersonCaption(doc.personId, persons)
                          return who ? ` · ${who}` : ''
                        })()}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-3">
                      <button
                        type="button"
                        disabled={busy || downloadBusyId === doc.id}
                        className="text-xs font-semibold text-[#25325A] hover:underline disabled:opacity-50"
                        onClick={() => void downloadDocFile(doc)}
                      >
                        {downloadBusyId === doc.id ? '…' : 'Download'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        className="text-xs font-semibold text-[#991B1B] hover:underline"
                        onClick={() => void removeDoc(doc.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {step === 10 && (
        <section className="space-y-4 rounded-2xl border px-6 py-6 shadow-sm" style={{ borderColor: '#C1C7D3' }}>
          <h2 className="text-lg font-semibold text-[#0C183B]">Review</h2>
          <p className="text-sm text-[#627396]">
            Final check before submitting for compliance review. Confirm the summary reflects your questionnaires and uploads.
          </p>
          {!canSubmitLocally ? (
            <p className="text-sm text-[#92400E]">
              Before submit: required company documents (certificate, proof of business address, articles), ID and address
              proof for every director and every beneficial owner (UBO), entity and director list on file. Optional document
              slots can stay empty. Server validates again — fix any gaps flagged in the messaging.
            </p>
          ) : (
            <p className="text-sm text-[#065F46]">
              Mandatory checks satisfied on-page. Submission may still fail if compliance flags unresolved items.
            </p>
          )}
          <dl className="space-y-1 text-sm">
            <div>
              <dt className="inline text-[#627396]">Legal name:&nbsp;</dt>
              <dd className="inline font-semibold text-[#28293C]">{String(entity.legalEntityName ?? '').trim() || '—'}</dd>
            </div>
            <div>
              <dt className="inline text-[#627396]">Director on file:&nbsp;</dt>
              <dd className="inline font-semibold text-[#28293C]">{hasDirectorInList ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="inline text-[#627396]">UBO on file:&nbsp;</dt>
              <dd className="inline font-semibold text-[#28293C]">{ubos.length > 0 ? `${ubos.length} registered` : 'No'}</dd>
            </div>
            <div>
              <dt className="inline text-[#627396]">Required document pack:&nbsp;</dt>
              <dd className="inline font-semibold text-[#28293C]">{canSubmitLocally ? 'Ready' : 'Incomplete'}</dd>
            </div>
          </dl>

          <div className="border-t border-[#E3E8EF] pt-6">
            <h3 className="text-sm font-semibold text-[#0C183B]">Submit application</h3>
            {!canSubmitLocally ? (
              <p className="mt-2 text-sm text-[#92400E]">
                Complete required documents and questionnaire items above before submitting.
              </p>
            ) : (
              <p className="mt-2 text-sm text-[#065F46]">Ready to submit.</p>
            )}
            <button
              type="button"
              disabled={busy || !canSubmitLocally}
              className="mt-4 rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(101deg, #415382 0%, #25325A 100%)' }}
              onClick={() =>
                void (async () => {
                  const ok = await finishSubmit()
                  if (ok) navigate(`/onboarding/${applicationId}`)
                })()
              }
            >
              Submit for review
            </button>
          </div>
        </section>
      )}

      {directorFioModalIx !== null ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 p-4"
          role="presentation"
          onMouseDown={() => setDirectorFioModalIx(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-md overflow-auto rounded-2xl border bg-white p-6 shadow-xl"
            style={{ borderColor: '#C1C7D3' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[#0C183B]">Legal name</h3>
            <p className="mt-1 text-sm text-[#627396]">
              Adjust spelling for this linked director. Other fields remain on the shareholder step.
            </p>
            <div className="mt-4 grid gap-3">
              <label className="block text-sm">
                <span className="text-[#627396]">First name</span>
                <input
                  className="mt-1 w-full rounded-lg border px-3 py-2"
                  style={{ borderColor: '#C1C7D3' }}
                  value={directorFioDraft.firstName}
                  onChange={(e) => setDirectorFioDraft((d) => ({ ...d, firstName: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="text-[#627396]">Middle name (optional)</span>
                <input
                  className="mt-1 w-full rounded-lg border px-3 py-2"
                  style={{ borderColor: '#C1C7D3' }}
                  value={directorFioDraft.middleName}
                  onChange={(e) => setDirectorFioDraft((d) => ({ ...d, middleName: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="text-[#627396]">Last name</span>
                <input
                  className="mt-1 w-full rounded-lg border px-3 py-2"
                  style={{ borderColor: '#C1C7D3' }}
                  value={directorFioDraft.lastName}
                  onChange={(e) => setDirectorFioDraft((d) => ({ ...d, lastName: e.target.value }))}
                />
              </label>
            </div>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-[#C1C7D3] bg-white px-4 py-2 text-sm font-semibold"
                onClick={() => setDirectorFioModalIx(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(101deg, #415382 0%, #25325A 100%)' }}
                onClick={() => {
                  const ix = directorFioModalIx
                  if (ix === null) return
                  patchShareholderRow(ix, {
                    firstName: directorFioDraft.firstName.trim(),
                    middleName: directorFioDraft.middleName.trim(),
                    lastName: directorFioDraft.lastName.trim(),
                  })
                  setDirectorFioModalIx(null)
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="sticky bottom-0 z-20 mt-8 flex flex-col gap-4 border-t border-[#DCE3EE] bg-[rgb(251_252_254_/0.97)] pb-6 pt-5 backdrop-blur-sm">
        {msg ? (
          <div
            ref={footerMsgRef}
            role="alert"
            className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm font-semibold leading-snug text-amber-950 shadow-[0_2px_14px_rgba(146,64,14,0.15)]"
          >
            {msg}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy || step === 0}
          className="rounded-lg border border-[#C1C7D3] bg-white px-4 py-2 text-sm font-semibold text-[#25325A] disabled:opacity-50"
          onClick={goPrev}
        >
          Back
        </button>
        {step < lastStepIndex ? (
          <button
            type="button"
            disabled={busy}
            className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(101deg, #415382 0%, #25325A 100%)' }}
            onClick={() => void goNext()}
          >
            Save & continue
          </button>
        ) : null}
        </div>
      </div>
    </div>
  )
}
